/**
 * The append-only correction log (M4.2; Constitution invariants 4 and 8). Two
 * operations exist: append and list. There is no update and no delete here or
 * anywhere in the app — "Clear all data" deletes the database whole. A
 * correction's id is a content hash, so appending the same correction twice is
 * refused rather than duplicated.
 */
import type { Correction, LedgerItem } from '@cordelia/core'
import { makeCorrection, previousValueOf } from '@cordelia/pipeline'
import type { CorrectionInput } from '@cordelia/pipeline'
import { db } from './db.js'

/** Appends one correction. `add`, never `put`: an existing id is an error, not an overwrite. */
export async function appendCorrection(correction: Correction): Promise<void> {
  await db().corrections.add(correction)
}

/** Every correction ever made in this browser, oldest first. */
export async function listCorrections(): Promise<readonly Correction[]> {
  return db().corrections.orderBy('time').toArray()
}

/**
 * Appends a correction about one ledger item, recording what the contested
 * field said before it. The item is read, never written: the prior value is
 * captured here so Trace can say what was previously believed without
 * reconstructing an earlier run.
 */
export async function appendCorrectionAbout(
  item: LedgerItem,
  input: Omit<CorrectionInput, 'affects' | 'previous_value'> &
    Partial<Pick<CorrectionInput, 'affects'>>,
): Promise<Correction> {
  const correction = makeCorrection({
    ...input,
    affects: input.affects ?? [item.id],
    previous_value: previousValueOf(
      item as unknown as Record<string, unknown>,
      input.contested_field,
    ),
  })
  await appendCorrection(correction)
  return correction
}
